import { PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { RemoteCombobox } from "@/components/ui/remote-combobox";
import { PageContainer, PageHeader } from "@/components/layout";
import AddItemDialog from "../../components/item/add-item-dialog";
import BFHierarchyTable from "../../components/hierarchy-table";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { ItemSheet } from "../../components/item/item-sheet";
import type { ItemRowForSheet } from "../../components/item/item-sheet";
import FiltersCard from "../../components/filters-card";
import { MediaViewer, photosToMedia } from "../../components/media-gallery";
import { TagsMultiSelect } from "../../components/tags-multi-select";
import { useQueryState } from "../../utils/query-state";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { Permissions } from "../../entities/permissions";
import { hasPermission } from "../login/selectors";
import {
  useDeleteItemsMutation,
  useItemListQuery,
} from "../../services/api";

import generateColumns from "./columns";

interface ItemRow {
  id: string | number;
  parentId?: string | number | null;
  title: string;
  description?: string;
  photos?: unknown;
  children?: ItemRow[];
  level?: number;
  [key: string]: unknown;
}

interface ItemsResponse {
  items?: ItemRow[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
}

interface AutocompleteValue {
  id?: string | number;
  title?: string;
  [key: string]: unknown;
}

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface ParentRef {
  id: string | number;
  title: string;
}

const DEFAULT_COLUMNS = new Set([
  "id",
  "parentId",
  "title",
  "description",
  "photos",
  "children",
  "level",
  "tags",
]);

/**
 * Recursive search through the hierarchy for an item with the given id.
 * Re-exported (named) because `item-flow-dialog` imports it from this
 * file's index path — preserves the cross-module API without forcing a
 * follow-up migration of that dialog.
 */
export function findObjectById(
  data: ItemRow[] | undefined | null,
  leaf: keyof ItemRow,
  id: string | number
): ItemRow | null {
  if (!data) return null;
  for (const item of data) {
    if (item.id === id) return item;
    const children = item[leaf] as ItemRow[] | undefined;
    if (children && children.length > 0) {
      const found = findObjectById(children, leaf, id);
      if (found) return found;
    }
  }
  return null;
}

export default function Items() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const hasAddPermission = useSelector(
    hasPermission(Permissions.PAGE_ITEMS_ADD)
  );
  const hasEditPermission = useSelector(
    hasPermission(Permissions.PAGE_ITEMS_EDIT)
  );
  const hasRemovePermission = useSelector(
    hasPermission(Permissions.PAGE_ITEMS_REMOVE)
  );

  const [itemsColumns, setItemsColumns] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<(string | number)[]>([]);
  const [openAddItemDialog, setOpenAddItemDialog] = useState(false);
  const [openDeleteItemDialog, setOpenDeleteItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemRow | Record<string, never>>({});
  const [parentToAddEdit, setParentToAddEdit] = useState<ParentRef | Record<string, never>>({});
  const [openGallery, setOpenGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);

  // Sheet open state in the URL so refresh and shared links restore it.
  const [selectedDetailId, setSelectedDetailId] = useQueryState({
    paramName: "detail",
    fromQuery: (value: string | null) => value || null,
    toQuery: (value: string | null) => (value ? String(value) : ""),
    defaultValue: null,
  }) as [string | null, (next: string | null) => void];
  const [editOnOpen, setEditOnOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<
    Record<string, string | number[] | undefined> | null
  >(null);

  // Filter form state.
  const [item, setItem] = useState<AutocompleteValue | null>(null);
  const [parentItem, setParentItem] = useState<AutocompleteValue | null>(null);
  const [filterTags, setFilterTags] = useState<TagRef[]>([]);

  const computedFilters = useMemo(() => {
    const obj: Record<string, string | number[] | undefined> = {
      id: item?.id != null ? String(item.id) : undefined,
      parentId: parentItem?.id != null ? String(parentItem.id) : undefined,
      tagIds: filterTags.length > 0 ? filterTags.map((tag) => tag.id) : undefined,
    };
    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined) delete obj[key];
    });
    return obj;
  }, [item, parentItem, filterTags]);

  function onSearch() {
    setFilters(computedFilters);
    setPage(0);
  }

  function onCancel() {
    setItem(null);
    setParentItem(null);
    setFilterTags([]);
    setFilters(null);
    setPage(0);
  }

  const { data: items } = useItemListQuery({
    range: { page: page + 1 },
    filters: filters ?? undefined,
  }) as { data?: ItemsResponse };
  const [deleteItems, { isLoading: isDeleting }] = useDeleteItemsMutation();

  // Extract the dynamic-field columns the user has on this workspace.
  // Same logic as the original page: take the keys of the first row,
  // drop the well-known structural ones, keep the rest as column ids.
  useEffect(() => {
    if (items?.items) {
      const first = items.items[0];
      const extra = first
        ? Object.keys(first).filter((key) => !DEFAULT_COLUMNS.has(key))
        : [];
      setItemsColumns(extra);
    }
  }, [items]);

  function onAdd(parentRow?: ItemRow) {
    if (openAddItemDialog) return;
    if (parentRow) {
      setParentToAddEdit({ id: parentRow.id, title: parentRow.title });
    }
    setOpenAddItemDialog(true);
  }

  function onView(row: ItemRow) {
    setSelectedItem(row);
    if (row.parentId != null) {
      const parent = findObjectById(items?.items, "children", row.parentId);
      if (parent) setParentToAddEdit({ id: parent.id, title: parent.title });
    } else {
      setParentToAddEdit({});
    }
    setEditOnOpen(false);
    setSelectedDetailId(String(row.id));
  }

  // Resolve the row backing the open sheet from the current list. If a
  // deep-linked id isn't on the current page the sheet renders an empty
  // state until the list catches up.
  const selectedSheetItem = useMemo(() => {
    if (!selectedDetailId) return null;
    return (
      findObjectById(items?.items, "children", selectedDetailId) ??
      (selectedItem as ItemRow | undefined) ??
      null
    );
  }, [selectedDetailId, items, selectedItem]);

  function onDelete() {
    if (openDeleteItemDialog) return;
    if (selectedItems.length === 1) {
      const single = findObjectById(items?.items, "children", selectedItems[0]);
      if (single) setSelectedItem(single);
    }
    setOpenDeleteItemDialog(true);
  }

  function onImageClick(images: string[]) {
    setGalleryImages(images);
    setOpenGallery(true);
  }

  async function onDeleted() {
    if (selectedItems.length === 0) {
      showNotification("error", t("common.api-error"));
      // Resolve normally — ConfirmDialog closes; the toast surfaces the
      // intent failure without leaving a stale modal up.
      setOpenDeleteItemDialog(false);
      return;
    }
    try {
      await deleteItems({ items: selectedItems }).unwrap();
      showNotification(
        "success",
        selectedItems.length === 1
          ? t("items.delete-item-dialog.delete-success", {
              name: (selectedItem as ItemRow).title,
            })
          : t("items.delete-items-dialog.delete-success", {
              count: selectedItems.length,
            })
      );
      setSelectedItems([]);
      setSelectedItem({});
      // Also close the sheet if it was showing one of the deleted rows.
      if (
        selectedDetailId &&
        selectedItems.some((id) => String(id) === selectedDetailId)
      ) {
        setSelectedDetailId(null);
      }
      setOpenDeleteItemDialog(false);
    } catch {
      showNotification("error", t("common.api-error"));
      // Re-throw so ConfirmDialog keeps itself open for retry.
      throw new Error("delete-failed");
    }
  }

  const itemsTableColumns = generateColumns({
    t,
    itemsColumns,
    onAdd,
    hasAddPermission,
    onImageClick,
  });

  return (
    <>
      <ConfirmDialog
        open={openDeleteItemDialog}
        onOpenChange={(next) => { if (!next) setOpenDeleteItemDialog(false); }}
        title={
          selectedItems.length === 1
            ? t("items.delete-item-dialog.title", {
                name: (selectedItem as ItemRow).title,
              })
            : t("items.delete-items-dialog.title", {
                count: selectedItems.length,
              })
        }
        confirmLabel={t("common.delete")}
        onConfirm={onDeleted}
        isPending={isDeleting}
      />
      <AddItemDialog
        title={t("items.add-item-dialog.title")}
        parent={parentToAddEdit}
        open={openAddItemDialog}
        onClose={() => {
          setOpenAddItemDialog(false);
          setParentToAddEdit({});
        }}
        onCreated={() => {
          setOpenAddItemDialog(false);
          setParentToAddEdit({});
        }}
      />
      <ItemSheet
        value={selectedSheetItem as ItemRowForSheet | null}
        parent={(parentToAddEdit as ParentRef) ?? null}
        open={selectedDetailId != null}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedDetailId(null);
            setSelectedItem({});
            setParentToAddEdit({});
          }
        }}
        canEdit={hasEditPermission}
        initialEditing={editOnOpen}
        onEdited={() => {
          showNotification(
            "success",
            t("items.edit-item-dialog.success", "Item updated"),
          );
        }}
        onDelete={
          hasRemovePermission && selectedSheetItem
            ? () => {
                setSelectedItems([selectedSheetItem.id]);
                setSelectedItem(selectedSheetItem as ItemRow);
                setOpenDeleteItemDialog(true);
              }
            : undefined
        }
      />
      <MediaViewer
        open={openGallery}
        onOpenChange={setOpenGallery}
        items={photosToMedia(galleryImages)}
        initialIndex={0}
      />
      <PageContainer title={t("items.tab-title")}>
        <PageHeader
          title={t("items.page-title")}
          description={t("items.info-message")}
          actions={
            hasAddPermission ? (
              <Button onClick={() => onAdd()}>
                <PlusIcon className="size-4" />
                {t("items.add-item")}
              </Button>
            ) : null
          }
        />

        <div className="mt-6">
          <FiltersCard onSearch={onSearch} onCancel={onCancel}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <RemoteCombobox
                resource="items/autocomplete"
                value={item}
                onChange={setItem}
                label={t("items.filter.item")}
              />
              <RemoteCombobox
                resource="items/autocomplete"
                value={parentItem}
                onChange={setParentItem}
                label={t("items.filter.parent-item")}
              />
              <TagsMultiSelect
                value={filterTags}
                onChange={setFilterTags}
                label={t("items.filter.tags")}
                placeholder={t("tags.multi-select.placeholder")}
                mode="filter"
              />
            </div>
          </FiltersCard>
        </div>

        {items && hasRemovePermission ? (
          <div className="mt-4">
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={selectedItems.length < 1}
            >
              <Trash2Icon className="size-4" />
              {t("items.delete-item")}
            </Button>
          </div>
        ) : null}

        {/* BFHierarchyTable handles the tree + multi-select + expandable
            rows; the shadcn flat DataTable doesn't have a tree variant
            yet. Row-click opens the sheet in view mode (same as every
            other migrated list page). */}
        <div className="mt-6">
          <BFHierarchyTable
            id="item"
            // BFHierarchyTable's prop type is loose (`array`); pass the
            // full Redux response — it pulls `.items` itself.
            data={items as unknown as unknown[]}
            columns={itemsTableColumns}
            leaf="children"
            page={page}
            onChangePage={(currentPage: number) => setPage(currentPage)}
            expandableRows
            selectableRows={hasRemovePermission}
            rowsSelected={selectedItems}
            onRowSelectionChange={setSelectedItems}
            onRowClick={onView}
          />
        </div>
      </PageContainer>
    </>
  );
}
